import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CaretRight,
  Heart,
  Sun,
  Moon,
  Folders,
  Clock,
  ProhibitInset,
  UsersThree,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import useParentBadges from "@/lib/hooks/useParentBadges";
import {
  listChildrenForParent,
  listReportsForChild,
} from "@/lib/dailyReports";
import {
  findActiveNotice,
  formatLocalTime,
  NOTICE_LABELS,
} from "@/lib/attendanceNotices";
import { ENTRY_TYPES, todayStr } from "@/lib/entryTypes";
import AnnouncementsCard from "@/components/parent/AnnouncementsCard";
import ComplianceCard from "@/components/parent/ComplianceCard";
import ClearanceChip from "@/components/compliance/ClearanceChip";
import UpcomingEventsCard from "@/components/parent/UpcomingEventsCard";
import PWAInstallCard from "@/components/PWAInstallCard";
import ParentOnboardingStrip from "@/components/parent/ParentOnboardingStrip";
import ChildAvatar from "@/components/children/ChildAvatar";
import AttendanceNoticeSheet from "@/components/attendance/AttendanceNoticeSheet";

/*
 * Parent Home — deliberately calm and short. Four zones, in order:
 *   1. Greeting + featured-child snapshot (identity & today's status)
 *   2. "Needs attention" — only renders when something actually does
 *   3. Today — announcements & upcoming events (self-hiding)
 *   4. Install nudge, last
 * Everything per-child (photo, pickup, meds, health, conferences,
 * documents, enrollment) lives on the Family tab now.
 */
export default function ParentDashboard() {
  const { profile } = useAuth();
  const firstName = (profile?.full_name || "there").split(" ")[0];
  const today = useMemo(() => todayStr(), []);
  const badges = useParentBadges(profile?.id);

  const [kids, setKids] = useState([]);
  const [child, setChild] = useState(null);
  const [latestReport, setLatestReport] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [activeNotice, setActiveNotice] = useState(null);
  const [noticeSheet, setNoticeSheet] = useState({ open: false, type: "absent" });

  const reloadActiveNotice = async (childId) => {
    if (!childId) return;
    try {
      const row = await findActiveNotice({ childId, date: today });
      setActiveNotice(row || null);
    } catch {
      setActiveNotice(null);
    }
  };

  useEffect(() => {
    if (!profile?.id) return;
    (async () => {
      try {
        const list = await listChildrenForParent(profile.id);
        setKids(list);
        const first = list[0];
        if (!first) {
          setChild(null);
          setLatestReport(null);
          return;
        }
        setChild(first);
        const reports = await listReportsForChild(first.id, 5);
        // Prefer today's report; otherwise show most recent.
        const todays = reports.find((r) => r.report_date === today);
        setLatestReport(todays || reports[0] || null);
        await reloadActiveNotice(first.id);
      } finally {
        setLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id, today]);

  const stats = useMemo(() => {
    const entries = latestReport?.entries || [];
    const count = (t) => entries.filter((e) => e.entry_type === t).length;
    return {
      mood: latestReport?.overall_mood || "—",
      meals: count(ENTRY_TYPES.MEAL),
      naps: count(ENTRY_TYPES.NAP),
    };
  }, [latestReport]);

  const needsAttention =
    badges.unacknowledgedIncidents > 0 || badges.missingDocs > 0;

  return (
    <div data-testid="parent-dashboard" className="space-y-5 sm:space-y-6">
      <section>
        <p className="overline text-primary-hover">Today</p>
        <h1 className="mt-2 font-heading text-2xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
          Hi {firstName}
        </h1>
        <p className="mt-1.5 text-sm text-neutral-600 sm:mt-2 sm:text-base">
          A calm snapshot of {child?.first_name || "your child"}'s day.
        </p>
      </section>

      {/* First-run — no child on file yet. */}
      {loaded && !child && (
        <Link
          to="/parent/enrollment"
          data-testid="parent-empty-no-child"
          className="block rounded-2xl border border-primary/20 bg-primary/5 p-5 transition-all hover:-translate-y-[2px] hover:shadow-md active:scale-[0.99]"
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary-hover">
            Welcome
          </p>
          <p className="mt-1 font-heading text-base font-semibold tracking-tight text-neutral-900">
            Let's get your little one set up
          </p>
          <p className="mt-1 text-sm leading-relaxed text-neutral-600">
            We don't have a child on file for your family yet. You can start an inquiry
            or check the status of an existing one.
          </p>
          <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary-hover">
            Open enrollment
            <CaretRight size={12} weight="bold" />
          </span>
        </Link>
      )}

      {child && (
        <section>
          <article className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-[0_2px_12px_rgba(20,22,26,0.04)]">
            <div className="flex items-start justify-between gap-4">
              <div className="flex min-w-0 items-center gap-4">
                <ChildAvatar
                  path={child.profile_photo_path}
                  name={`${child.first_name || ""} ${child.last_name || ""}`}
                  size={56}
                  testId={`parent-child-avatar-${child.id}`}
                />
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
                    Child
                  </p>
                  <h2 className="mt-1 font-heading text-xl font-semibold tracking-tight text-neutral-900">
                    {child.first_name} {child.last_name || ""}
                  </h2>
                  <ClearanceChip
                    childId={child.id}
                    resolveHref="/parent/health-records"
                    resolveLabel="Open health records"
                    testIdPrefix="parent-child-clearance"
                  />
                </div>
              </div>
              {latestReport?.report_date === today ? (
                <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary-hover">
                  Report ready
                </span>
              ) : (
                <span className="rounded-full bg-neutral-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-neutral-600">
                  Not yet logged
                </span>
              )}
            </div>
            {/* Today's status — either an active notice or two file-a-notice CTAs. */}
            {activeNotice ? (
              <div
                data-testid="parent-active-notice"
                data-notice-type={activeNotice.notice_type}
                className={`mt-5 flex flex-wrap items-center gap-3 rounded-2xl border px-3 py-2 text-sm ${
                  activeNotice.notice_type === "absent"
                    ? "border-danger/30 bg-danger-soft text-danger"
                    : "border-warning/40 bg-warning-soft text-warning"
                }`}
              >
                {activeNotice.notice_type === "absent" ? (
                  <ProhibitInset size={16} weight="duotone" />
                ) : (
                  <Clock size={16} weight="duotone" />
                )}
                <p className="min-w-0 flex-1">
                  <strong>{NOTICE_LABELS[activeNotice.notice_type]} today</strong>
                  {activeNotice.notice_type === "late" && activeNotice.expected_arrival_time
                    ? ` · ~${formatLocalTime(activeNotice.expected_arrival_time)}`
                    : ""}
                  {activeNotice.reason ? ` · "${activeNotice.reason}"` : ""}
                  <span className="block text-[11px] text-neutral-600/80">
                    Your teacher has been notified.
                  </span>
                </p>
                <Link
                  to="/parent/absences"
                  data-testid="parent-active-notice-manage"
                  className="rounded-full border border-current/40 bg-white/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider hover:bg-white"
                >
                  Manage
                </Link>
              </div>
            ) : (
              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="parent-dashboard-report-absent"
                  onClick={() => setNoticeSheet({ open: true, type: "absent" })}
                  className="inline-flex items-center gap-1.5 rounded-full border border-danger/40 bg-white px-3 py-1.5 text-xs font-semibold text-danger hover:bg-danger-soft"
                >
                  <ProhibitInset size={12} weight="duotone" />
                  Report absence
                </button>
                <button
                  type="button"
                  data-testid="parent-dashboard-report-late"
                  onClick={() => setNoticeSheet({ open: true, type: "late" })}
                  className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-50"
                >
                  <Clock size={12} weight="duotone" />
                  Running late
                </button>
                <Link
                  to="/parent/absences"
                  data-testid="parent-dashboard-absences-link"
                  className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-200"
                >
                  History
                  <CaretRight size={12} weight="bold" />
                </Link>
              </div>
            )}
            <div className="mt-6 grid grid-cols-3 gap-3">
              <MiniStat icon={Sun} label="Mood" value={cap(stats.mood)} />
              <MiniStat icon={Heart} label="Meals" value={String(stats.meals)} />
              <MiniStat icon={Moon} label="Naps" value={String(stats.naps)} />
            </div>
            {kids.length > 1 && (
              <Link
                to="/parent/family"
                data-testid="parent-open-family"
                className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-primary-hover"
              >
                <UsersThree size={14} weight="duotone" />
                See all {kids.length} children
                <CaretRight size={12} weight="bold" />
              </Link>
            )}
          </article>
        </section>
      )}

      {/* ---- Needs attention — renders only when something does. ---- */}
      {needsAttention && (
        <section className="space-y-3" data-testid="parent-attention-zone">
          <p className="overline text-primary-hover">Needs attention</p>

          {badges.unacknowledgedIncidents > 0 && (
            <Link
              to="/parent/incidents"
              data-testid="parent-open-incidents"
              className="flex items-center gap-4 rounded-2xl border border-[#C6851E]/30 bg-[#C6851E]/5 p-5 transition-all hover:-translate-y-[2px] hover:shadow-md active:scale-[0.99]"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#C6851E]/10">
                <Sun size={22} weight="duotone" className="text-[#C6851E]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-heading text-base font-semibold tracking-tight text-neutral-900">
                  {badges.unacknowledgedIncidents === 1
                    ? "1 incident report needs your acknowledgement"
                    : `${badges.unacknowledgedIncidents} incident reports need your acknowledgement`}
                </p>
                <p className="text-sm text-neutral-500">Tap to review and acknowledge.</p>
              </div>
              <span
                data-testid="parent-incidents-badge"
                className="flex h-6 min-w-[24px] items-center justify-center rounded-full bg-danger px-2 text-xs font-bold text-white"
              >
                {badges.unacknowledgedIncidents}
              </span>
            </Link>
          )}

          {badges.missingDocs > 0 && (
            <Link
              to="/parent/documents"
              data-testid="parent-open-documents"
              className="flex items-center gap-4 rounded-2xl border border-amber-300 bg-amber-50/60 p-5 transition-all hover:-translate-y-[2px] hover:shadow-md active:scale-[0.99]"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100 text-amber-800">
                <Folders size={22} weight="duotone" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-heading text-base font-semibold tracking-tight text-neutral-900">
                  {badges.missingDocs === 1
                    ? "1 document needs your attention"
                    : `${badges.missingDocs} documents need your attention`}
                </p>
                <p className="text-sm text-neutral-500">
                  Upload, sign, or replace expired forms to stay compliant.
                </p>
              </div>
              <span
                data-testid="parent-docs-badge"
                className="flex h-6 min-w-[24px] items-center justify-center rounded-full bg-amber-700 px-2 text-xs font-bold text-white"
              >
                {badges.missingDocs}
              </span>
            </Link>
          )}
        </section>
      )}

      {/* First-7-days onboarding strip — supports the daily snapshot without
          taking priority over it or urgent parent actions. */}
      {child && <ParentOnboardingStrip profile={profile} />}

      {/* Compliance nudge — self-hiding when everything is in order. */}
      <ComplianceCard profileId={profile?.id} />

      {/* ---- Today at the center — both cards self-hide when empty. ---- */}
      <AnnouncementsCard />
      <UpcomingEventsCard />

      {/* Install nudge, last — never above real content. */}
      <PWAInstallCard />

      <AttendanceNoticeSheet
        open={noticeSheet.open}
        onOpenChange={(o) => setNoticeSheet((s) => ({ ...s, open: o }))}
        initialType={noticeSheet.type}
        child={child}
        submittedBy={profile?.id}
        onSubmitted={() => reloadActiveNotice(child?.id)}
      />
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-neutral-100/70 p-3">
      <Icon size={18} weight="duotone" className="text-primary-hover" />
      <p className="mt-2 text-[11px] font-bold uppercase tracking-wider text-neutral-500">
        {label}
      </p>
      <p className="mt-0.5 text-sm font-semibold text-neutral-900">{value}</p>
    </div>
  );
}

function cap(s) {
  if (!s || s === "—") return "—";
  return s.charAt(0).toUpperCase() + s.slice(1);
}
